import {
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { PreventiveMaintenancePlanPriority } from '@evimo/types/enums/preventive-maintenance-plan-priority';
import { Button } from '~/app/components/common/Button';
import { StepGeneralSchedulingHorizonHelp } from '~/app/components/maintenance/preventive/wizard/StepGeneralSchedulingHorizonHelp/StepGeneralSchedulingHorizonHelp';
import type { WizardData } from '~/app/components/maintenance/preventive/wizard/types';
import { generatePlanCode } from '~/app/components/maintenance/preventive/wizard/utils';

export interface PlanWizardStepGeneralProps {
  data: WizardData;
  onChange: (patch: Partial<WizardData>) => void;
}

export function PlanWizardStepGeneral({
  data,
  onChange,
}: PlanWizardStepGeneralProps) {
  const { t } = useTranslation('maintenance');

  return (
    <Card>
      <Form layout="vertical">
        <Row gutter={[24, 16]} align="top">
          <Col xs={24} lg={14} xl={16}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={t('wizard.general.code')} required>
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      value={data.code}
                      onChange={(e) => onChange({ code: e.target.value })}
                      placeholder={t('wizard.general.codePlaceholder')}
                    />
                    <Button
                      type="default"
                      onClick={() => onChange({ code: generatePlanCode(20) })}
                    >
                      {t('wizard.general.generateCode')}
                    </Button>
                  </Space.Compact>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('wizard.general.name')} required>
                  <Input
                    value={data.name}
                    onChange={(e) => onChange({ name: e.target.value })}
                    placeholder={t('wizard.general.namePlaceholder')}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label={t('wizard.general.priority')}>
                  <Select
                    value={data.priority}
                    onChange={(v) => onChange({ priority: v })}
                    options={Object.values(
                      PreventiveMaintenancePlanPriority,
                    ).map((v) => ({
                      label: t(`priority.${v}`),
                      value: v,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={t('wizard.general.schedulingHorizonDays')}
                  tooltip={t('wizard.general.schedulingHorizonDaysHelp')}
                  required
                >
                  <InputNumber
                    min={1}
                    precision={0}
                    style={{ width: '100%' }}
                    value={data.schedulingHorizonDays}
                    onChange={(v) =>
                      onChange({
                        schedulingHorizonDays:
                          typeof v === 'number' && v >= 1 ? v : 90,
                      })
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={t('wizard.general.active')}>
                  <Switch
                    checked={data.active}
                    onChange={(v) => onChange({ active: v })}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col xs={24} lg={10} xl={8}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <StepGeneralSchedulingHorizonHelp />
            </div>
          </Col>
        </Row>
      </Form>
    </Card>
  );
}
